import * as React from "react"
import { Link, useLocation, useSearchParams } from "react-router-dom"
import { analyticsTabsFor } from "@/lib/routing/analyticsTabs"
import { cn } from "@/lib/utils"

// When the dashboard shell renders the tab bar itself (flush under the header,
// Paper "App v3 · Analytics Overview"), page-level instances render nothing.
const ShellTabsContext = React.createContext(false)

export function AnalyticsTabsInShell({ children }: { children: React.ReactNode }) {
  return <ShellTabsContext.Provider value={true}>{children}</ShellTabsContext.Provider>
}

interface Tab {
  id: string
  label: string
  href: string
}

interface AnalyticsTabBarProps {
  basePath: string
  className?: string
  /** "shell" = rendered by DashboardLayout, full-bleed under the header. */
  placement?: "page" | "shell"
}

export function AnalyticsTabBar({ basePath, className, placement = "page" }: AnalyticsTabBarProps) {
  const renderedByShell = React.useContext(ShellTabsContext)
  const isShareMode = basePath.startsWith("/s/")
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const [showScrollIndicator, setShowScrollIndicator] = React.useState(false)
  const [scrollProgress, setScrollProgress] = React.useState(0)

  React.useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const checkOverflow = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth
      setShowScrollIndicator(hasOverflow)
    }

    const handleScroll = () => {
      const maxScroll = el.scrollWidth - el.clientWidth
      if (maxScroll > 0) {
        setScrollProgress(el.scrollLeft / maxScroll)
      }
    }

    checkOverflow()
    handleScroll()

    el.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', checkOverflow)

    return () => {
      el.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', checkOverflow)
    }
  }, [])

  // Five tabs (Paper "Home + Analytics", Oct 2026). Retired slugs redirect via
  // AnalyticsRedirect; GA4 stays routable but is not a tab.
  const tabs: Tab[] = analyticsTabsFor(isShareMode).map((tab) => ({
    id: tab.id,
    label: tab.label,
    href: `${basePath}/${tab.slug}`,
  }))

  const isActive = (tab: Tab) => {
    const currentPath = location.pathname
    // Check if current path matches or starts with tab href
    if (tab.id === "overview") {
      // Overview is active for /traffic path specifically
      return currentPath === tab.href || currentPath === `${basePath}/traffic/`
    }
    if (tab.id === "split-testing") {
      return currentPath.startsWith(tab.href) || currentPath.includes("/split-tests/")
    }
    if (tab.id === "funnels") {
      return currentPath.startsWith(tab.href)
    }
    return currentPath.startsWith(tab.href)
  }

  if (placement === "page" && renderedByShell) return null

  // Paper: 44px tabs, 22px apart, 13px/500; active = ink label with a 2px ink
  // underline (inset shadow); inactive 60% ink; hairline under the row.
  return (
    <div
      className={cn(
        "relative w-full border-b border-black/[0.06] dark:border-white/[0.07]",
        placement === "shell" ? "shrink-0 bg-background px-4 sm:px-9" : "mb-6",
        className
      )}
    >
      <nav
        ref={scrollRef}
        aria-label="Analytics"
        className="flex flex-row w-full overflow-x-auto scrollbar-hide gap-[22px]"
        role="tablist"
      >
        {tabs.map((tab) => {
          const active = isActive(tab)

          return (
            <Link
              key={tab.id}
              to={{ pathname: tab.href, search: searchParams.toString() }}
              role="tab"
              aria-selected={active}
              aria-current={active ? "page" : undefined}
              className={cn(
                "relative flex h-11 shrink-0 items-center whitespace-nowrap outline-none select-none",
                "text-[13px] font-medium leading-4 transition-colors duration-150",
                "focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:rounded-md",
                active
                  ? "text-foreground shadow-[inset_0_-2px_0_var(--foreground)]"
                  : "text-foreground/60 hover:text-foreground"
              )}
            >
              {tab.label}
            </Link>
          )
        })}
      </nav>

      {/* Scroll hint — only when the row overflows */}
      {showScrollIndicator && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent"
          style={{ opacity: scrollProgress >= 0.98 ? 0 : 1 }}
        />
      )}
    </div>
  )
}

export default AnalyticsTabBar
